import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProjectCreationFlow } from './ProjectCreationFlow';
import { useWorkspace } from '@/store/workspace';
import { getWorkspaceProjects } from '@/lib/database';
import { ActionBarProvider, useActionBarValue } from '@/contexts/ActionBarContext';

export function FirstProjectSetup() {
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();

  useEffect(() => {
    const checkExistingProjects = async () => {
      if (!currentWorkspace) {
        setIsChecking(false);
        return;
      }

      try {
        const projects = await getWorkspaceProjects(currentWorkspace.id);
        if (projects.length > 0) {
          navigate('/home', { replace: true });
          return;
        }
      } catch (err) {
        console.error('Error checking projects:', err);
      } finally {
        setIsChecking(false);
      }
    };

    checkExistingProjects();
  }, [currentWorkspace, navigate]);

  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ingesta-blue-50 to-ingesta-teal-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-ingesta-blue-600" />
      </div>
    );
  }

  if (!currentWorkspace) {
    return null;
  }

  const skipButton = (
    <Button
      variant="ghost"
      className="text-gray-500 hover:text-gray-700"
      onClick={() => navigate('/home')}
    >
      Skip for now
    </Button>
  );

  return (
    <ActionBarProvider>
      <div className="h-screen bg-gradient-to-br from-ingesta-blue-50 to-ingesta-teal-50 flex flex-col">
        <div className="flex-1 overflow-y-auto flex items-center justify-center p-4">
          <ProjectCreationFlow
            workspaceId={currentWorkspace.id}
            workspaceName={currentWorkspace.name}
            onCreated={(id) => navigate(`/projects/${id}`, { state: { fromCreate: true } })}
            onExit={() => navigate('/home')}
            enableDraft={false}
            choiceTitle="Create Your First Project"
            choiceInfoBox={
              <div className="bg-ingesta-blue-50 p-6 rounded-lg border border-ingesta-blue-200">
                <h4 className="font-semibold text-ingesta-blue-900 mb-2">
                  💡 Pro Tip
                </h4>
                <p className="text-sm text-ingesta-blue-800">
                  If you have existing project documents, emails, or notes, uploading them first
                  will save you time and give you AI-powered insights right from the start.
                </p>
              </div>
            }
            extraFooterActions={skipButton}
          />
        </div>
        <FirstProjectActionBarSlot />
      </div>
    </ActionBarProvider>
  );
}

function FirstProjectActionBarSlot() {
  const actionBar = useActionBarValue();
  if (!actionBar) return null;
  return (
    <div className="border-t border-gray-200 bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.05)] flex-shrink-0">
      {actionBar}
    </div>
  );
}
